import { FC, useEffect, useState } from 'react';
import { Button, Flex, FormControl, FormLabel, Input, SimpleGrid } from '@chakra-ui/react';

import { DistributorOutletMutation } from '@app/api/models/distributor';
import { SelectField, SwitchField, TextField } from '@app/components/common';
import AutocompleteField from '@app/components/common/autocomplete-field';
import Dialog from '@app/components/common/modals/dialog';
import { useQueryTable } from '@app/components/common/table/query-table';
import { useAlert, useApi, useAutocomplete } from '@app/hooks';
import { usePaginatedAutocomplete } from '@app/hooks/use-autocomplete';

import { DistributorOutletRow } from './columns';

type EditDistributorOutletProps = {
  open: boolean;
  initial?: DistributorOutletRow;
  segments: EntityBase[];
  onClose: () => void;
};

const emptyText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const toEntity = (id?: number | null, name?: string): EntityBase | undefined => (
  id ? { id, name: name || `#${id}` } : undefined
);

export const EditDistributorOutlet: FC<EditDistributorOutletProps> = ({
  open,
  initial,
  segments,
  onClose,
}) => {
  const api = useApi();
  const { success, warning } = useAlert();
  const { refetch } = useQueryTable();
  const getAutocomplete = useAutocomplete();
  const getPaginatedAutocomplete = usePaginatedAutocomplete();
  const isEdit = !!initial;

  const [loading, setLoading] = useState(false);
  const [distributor, setDistributor] = useState<EntityBase>();
  const [outlet, setOutlet] = useState<EntityBase>();
  const [route, setRoute] = useState<EntityBase>();
  const [segment, setSegment] = useState<EntityBase>();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [inn, setInn] = useState('');
  const [kpp, setKpp] = useState('');
  const [subnet, setSubnet] = useState('');
  const [status, setStatus] = useState('');
  const [openDate, setOpenDate] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [sfaId, setSfaId] = useState('');
  const [isGeneral, setIsGeneral] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) {
      return;
    }
    setDistributor(toEntity(initial?.distributor_id, initial?.distributor_name));
    setOutlet(toEntity(initial?.outlet_id, initial?.outlet_id ? `ТТ #${initial.outlet_id}` : undefined));
    setRoute(toEntity(initial?.route_number_id, initial?.route_number_id ? `Маршрут #${initial.route_number_id}` : undefined));
    setSegment(
      initial?.segment_id
        ? segments.find((item) => Number(item.id) === Number(initial.segment_id))
          || { id: initial.segment_id, name: initial.segment_name || `#${initial.segment_id}` }
        : undefined
    );
    setCode(initial?.distributor_outlet_id || '');
    setName(initial?.distributor_outlet_name || '');
    setLegalName(initial?.distributor_outlet_legal_name || '');
    setTitle(initial?.distributor_outlet_title || '');
    setAddress(initial?.distributor_outlet_address || '');
    setLegalAddress(initial?.distributor_outlet_legal_address || '');
    setInn(initial?.distributor_outlet_inn || '');
    setKpp(initial?.distributor_outlet_kpp || '');
    setSubnet(initial?.subnet || '');
    setStatus(initial?.status || '');
    setOpenDate(initial?.open_date || '');
    setCloseDate(initial?.close_date || '');
    setSfaId(initial?.sfa_id ? String(initial.sfa_id) : '');
    setIsGeneral(!!initial?.is_general);
    setIsActive(initial?.is_active ?? true);
  }, [open, initial, segments]);

  const onSubmit = async () => {
    const distributorId = distributor?.id ? Number(distributor.id) : undefined;
    if (!distributorId) {
      warning('Выберите дистрибьютора');
      return;
    }
    const sfa = sfaId.trim();
    if (sfa && !/^\d+$/.test(sfa)) {
      warning('SFA ID должен быть числом');
      return;
    }

    const payload: DistributorOutletMutation = {
      distributor_id: distributorId,
      outlet_id: outlet?.id ? Number(outlet.id) : null,
      is_general: isGeneral,
      distributor_outlet_id: emptyText(code),
      distributor_outlet_name: emptyText(name),
      distributor_outlet_legal_name: emptyText(legalName),
      distributor_outlet_title: emptyText(title),
      distributor_outlet_address: emptyText(address),
      distributor_outlet_legal_address: emptyText(legalAddress),
      distributor_outlet_inn: emptyText(inn),
      distributor_outlet_kpp: emptyText(kpp),
      subnet: emptyText(subnet),
      route_number_id: route?.id ? Number(route.id) : null,
      status: emptyText(status),
      open_date: openDate || null,
      close_date: closeDate || null,
      segment_id: segment?.id ? Number(segment.id) : null,
      sfa_id: sfa ? Number(sfa) : null,
      is_active: isActive,
    };

    setLoading(true);
    try {
      const response = isEdit && initial
        ? await api.distributors.updateDistributorOutlet({ ...payload, id: initial.id })
        : await api.distributors.createDistributorOutlet(payload);
      if (response?.code !== 200 && response?.code !== 201) {
        setLoading(false);
        return;
      }
      if (response.code === 200) {
        success(isEdit ? 'Запись сохранена' : 'Запись добавлена');
      }
      onClose();
      refetch();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      loading={loading}
      title={isEdit ? 'Изменение ТТ дистрибьютора' : 'Новая ТТ дистрибьютора'}
      width={760}
      actions={
        <Flex justifyContent="space-between" w="100%">
          <Button colorScheme="brand" onClick={onSubmit} isLoading={loading}>
            {isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
          <Button onClick={onClose}>Отмена</Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={3}>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
          <AutocompleteField
            id="distributor"
            name="distributor"
            label="Дистрибьютор"
            required
            value={distributor}
            onFetchOptions={getAutocomplete}
            onChange={setDistributor}
          />
          <AutocompleteField
            id="outlet"
            name="outlet"
            label="Торговая точка SNEG"
            value={outlet}
            onFetchOptions={getAutocomplete}
            onChange={setOutlet}
          />
          <TextField id="code" label="Код клиента" value={code} onChange={setCode} />
          <TextField id="name" label="Название" value={name} onChange={setName} />
          <TextField id="title" label="Вывеска" value={title} onChange={setTitle} />
          <TextField id="legal_name" label="Юр. название" value={legalName} onChange={setLegalName} />
          <TextField id="address" label="Адрес" value={address} onChange={setAddress} />
          <TextField id="legal_address" label="Юр. адрес" value={legalAddress} onChange={setLegalAddress} />
          <TextField id="inn" label="ИНН" value={inn} onChange={setInn} />
          <TextField id="kpp" label="КПП" value={kpp} onChange={setKpp} />
          <TextField id="subnet" label="Подсеть" value={subnet} onChange={setSubnet} />
          <TextField id="status" label="Статус" value={status} onChange={setStatus} />
          <AutocompleteField
            id="route"
            name="route_number"
            label="Маршрут"
            value={route}
            onFetchOptions={getPaginatedAutocomplete}
            onChange={setRoute}
          />
          <SelectField
            id="segment"
            label="Сегмент"
            options={segments}
            value={segment}
            onChange={setSegment}
          />
          <TextField id="sfa_id" label="SFA ID" value={sfaId} onChange={setSfaId} />
          <FormControl variant="floating" id="open_date">
            <Input
              type="date"
              placeholder="Дата открытия"
              value={openDate}
              onChange={(event) => setOpenDate(event.target.value)}
            />
            <FormLabel>Дата открытия</FormLabel>
          </FormControl>
          <FormControl variant="floating" id="close_date">
            <Input
              type="date"
              placeholder="Дата закрытия"
              value={closeDate}
              onChange={(event) => setCloseDate(event.target.value)}
            />
            <FormLabel>Дата закрытия</FormLabel>
          </FormControl>
        </SimpleGrid>
        <SwitchField id="is_general" label="Основной дистрибьютор точки" value={isGeneral} onChange={setIsGeneral} />
        <SwitchField id="is_active" label="Активна" value={isActive} onChange={setIsActive} />
      </Flex>
    </Dialog>
  );
};
